import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IUserAttributeMapping } from 'types/api';
import { useIncrementedID } from './incrementedID';

export const ID = Symbol('ID');

export type WithID<T> = T & { [ID]: number };

interface UseMappingEditorParams<T> {
	initialItems?: T[] | null | undefined;
	setExternalItems?: (items: T[]) => void;
	initializeItems?: (items: T[]) => T[];
	createEmptyItem: () => T;
	enabled?: boolean;
}

interface UseMappingEditorReturn<T> {
	rows: WithID<T>[];
	addItem: () => void;
	editItem: <K extends keyof T>(
		index: number,
		attr: K,
		value: T[K],
		persistNow?: boolean
	) => void;
	editItems: (index: number, patch: Partial<T>, persistNow?: boolean) => void;
	deleteItem: (index: number) => void;
	persist: () => void;
	isReady: boolean;
}

export const useMappingEditor = <T = IUserAttributeMapping>({
	initialItems,
	setExternalItems,
	initializeItems,
	createEmptyItem,
	enabled = true
}: UseMappingEditorParams<T>): UseMappingEditorReturn<T> => {
	const { getID } = useIncrementedID();

	const [internalItems, setInternalItems] = useState<WithID<T>[] | null>(null);
	const isDirtyRef = useRef(false);

	useEffect(() => {
		if (internalItems !== null || !initialItems || !enabled) return;
		const items = initializeItems
			? initializeItems(initialItems)
			: initialItems;
		setInternalItems(
			items.map((item) => ({
				...item,
				[ID]: getID()
			}))
		);
	}, [initialItems, internalItems, initializeItems, getID, enabled]);

	const rows = useMemo(() => internalItems ?? [], [internalItems]);

	const addItem = useCallback(() => {
		const updated = [...rows, { ...createEmptyItem(), [ID]: getID() }];
		setInternalItems(updated);
		setExternalItems?.(updated);
		isDirtyRef.current = false;
	}, [rows, createEmptyItem, getID, setExternalItems]);

	// Fields that depend on each other have to move together: two editItem calls in one handler
	// both map over the rows of the render they were created in, so the second discards the first.
	const editItems = useCallback(
		(index: number, patch: Partial<T>, persistNow?: boolean) => {
			const updated = rows.map((item, i) =>
				i === index ? { ...item, ...patch } : item
			);
			setInternalItems(updated);

			if (persistNow) {
				setExternalItems?.(updated);
				isDirtyRef.current = false;
			} else {
				isDirtyRef.current = true;
			}
		},
		[rows, setExternalItems]
	);

	const editItem = useCallback(
		<K extends keyof T>(
			index: number,
			attr: K,
			value: T[K],
			persistNow?: boolean
		) =>
			editItems(index, { [attr]: value } as unknown as Partial<T>, persistNow),
		[editItems]
	);

	const deleteItem = useCallback(
		(index: number) => {
			const updated = rows.filter((_, i) => i !== index);
			setInternalItems(updated);
			setExternalItems?.(updated);
			isDirtyRef.current = false;
		},
		[rows, setExternalItems]
	);

	const persist = useCallback(() => {
		if (isDirtyRef.current && internalItems) {
			setExternalItems?.(internalItems);
			isDirtyRef.current = false;
		}
	}, [internalItems, setExternalItems]);

	return useMemo(
		() => ({
			rows,
			addItem,
			editItem,
			editItems,
			deleteItem,
			persist,
			isReady: internalItems !== null
		}),
		[rows, addItem, editItem, editItems, deleteItem, persist, internalItems]
	);
};
